import { Injectable } from "@angular/core";
import { Observable, lastValueFrom, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { AccountsService } from "@apps/patient-app/src/app/services/accounts.service";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { GRAPHQL_OPERATION_NAMES, HttpService } from "@apps/patient-core/src/app/services/http.service";
import { PatientShortCodeBase } from "@backend/graph/patient-short-codes/patient-short-code-base";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { Constants } from "@apps/patient-app/constants";

interface I_ShortCodeResponse {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: Array<any>;
  data: {
    patient_short_code: PatientShortCodeBase;
  };
}

@Injectable({
  providedIn: "root",
})
export class ShortCodesService {
  constructor(
    private _http: HttpService,
    public _authService: BaseAuthService,
    private _accountsService: AccountsService,
    private _cacheService: CacheService
  ) {}

  public get short_code_id(): string | null {
    return this._cacheService.getSession(Constants.SHORT_CODE_ID_STORAGE_KEY);
  }

  public set short_code_id(short_code_id: string | null) {
    if (!short_code_id) {
      this._cacheService.deleteSession(Constants.SHORT_CODE_ID_STORAGE_KEY);
      return;
    }
    this._cacheService.setSession(Constants.SHORT_CODE_ID_STORAGE_KEY, short_code_id);
  }

  public getShortCode(short_code: string, date_of_birth: string): Observable<I_ShortCodeResponse> {
    const query = `{
      patient_short_code(id: "${short_code}", date_of_birth: "${date_of_birth}") {
        id
        path
        data
        jwt
        referrer
      }
    }`;

    return this._http.query<PatientShortCodeBase>(GRAPHQL_OPERATION_NAMES.SHORT_CODE, query).pipe(catchError((error) => of({ errors: [error], data: null })));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteShortCode(short_code: string): Observable<any> {
    const query = `{
      deletePatientShortCode(id: "${short_code}")
    }`;

    return this._http.mutation("deletePatientShortCode", query);
  }

  /**
   * Deletes the short code so that it can't be used anymore and signs the patient out
   */
  public async actionComplete(): Promise<void> {
    if (!this._authService.isPatientUnauthenticated()) return;
    this.short_code_id = null;
    await lastValueFrom(this.deleteShortCode(this._authService.getJWT("sid")));
    this._accountsService.forcedAmountToPay = null;
  }
}
